import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { collection, query, where, getDocs, addDoc, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { ChevronDown, ChevronUp, Search, RefreshCw, AlertCircle, Edit, Save, X, DollarSign, CheckCircle, Clock, Filter, Download, Star, Users, Gift, Heart, User, Trash2 } from 'lucide-react';
import { formatCurrency, formatDate, getRegistrationFeeLabel } from '../../utils/helpers';
import telemetry from '../../services/telemetry';

const PaymentManagement = ({ clubId, expenses, walletTransactions = [], teams, games: propGames, tournaments, onBack, players: propPlayers = [], isTreasurer = false, isAdmin = false, currentUserId, user, paymentSubmissions = [], onApprovePayment, onDenyPayment, clubName, onDeleteWalletTransaction }) => {
  const [players, setPlayers] = useState([]);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);

  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedTournament, setSelectedTournament] = useState('all');
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [registrationForms, setRegistrationForms] = useState({});

  // Filters State
  const [searchParams] = useSearchParams();
  const [selectedPlayers, setSelectedPlayers] = useState([]); // Multi-select IDs
  const [showReviewOnly, setShowReviewOnly] = useState(searchParams.get('review') === 'true'); // Toggle for pending reviews
  const [playerSearchTerm, setPlayerSearchTerm] = useState('');
  const [isPlayerDropdownOpen, setIsPlayerDropdownOpen] = useState(false);
  const dropdownRef = React.useRef(null);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsPlayerDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Waiver Modal State
  const [showWaiverModal, setShowWaiverModal] = useState(false);
  const [waiverTarget, setWaiverTarget] = useState(null); // { player, fee }
  const [waiverType, setWaiverType] = useState('full'); // 'full' or 'partial'
  const [waiverAmount, setWaiverAmount] = useState('');
  const [waiverReason, setWaiverReason] = useState('');

  // Payment Modal State
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentTarget, setPaymentTarget] = useState(null); // player object
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('Zelle'); // Default to Zelle
  const [paymentNote, setPaymentNote] = useState('');

  // Settle Debt Modal State
  const [showSettleModal, setShowSettleModal] = useState(false);
  const [settleTarget, setSettleTarget] = useState(null);

  // Edit Note Modal State
  const [showEditNoteModal, setShowEditNoteModal] = useState(false);
  const [editNoteTarget, setEditNoteTarget] = useState(null);
  const [editNoteText, setEditNoteText] = useState('');
  const [editNoteTournamentId, setEditNoteTournamentId] = useState('');
  const [settleAmount, setSettleAmount] = useState('');

  // Review Modal State
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewTarget, setReviewTarget] = useState(null); // player object containing pending submissions

  // Update Fee Modal State
  const [showUpdateFeeModal, setShowUpdateFeeModal] = useState(false);
  const [updateFeeTarget, setUpdateFeeTarget] = useState(null); // { player, fee }
  const [updateFeeAmount, setUpdateFeeAmount] = useState('');
  const [updateFeeReason, setUpdateFeeReason] = useState('');

  // Approve Payment Modal State
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approveTarget, setApproveTarget] = useState(null);
  const [approveDate, setApproveDate] = useState(new Date().toISOString().split('T')[0]);
  const [approveNote, setApproveNote] = useState('');

  // Reimbursement Modal State
  const [showReimbursementModal, setShowReimbursementModal] = useState(false);
  const [reimbursementTarget, setReimbursementTarget] = useState(null);
  const [reimbursementAmount, setReimbursementAmount] = useState('');
  const [reimbursementNote, setReimbursementNote] = useState('');

  // walletTransactions is passed as prop with real-time updates from AdminDashboard

  useEffect(() => {
    loadData();
  }, [teams, expenses, propGames, clubId, propPlayers]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Use players from props directly
      // This ensures consistency with AdminDashboard and avoids secondary unnecessary queries
      setPlayers(propPlayers);

      // Use games from props
      setGames(propGames || []);

      // Fetch Registration Forms for accurate status
      try {
        const formsRef = collection(db, 'registrationForms');
        const formsSnapshot = await getDocs(formsRef);
        const formsData = {};
        formsSnapshot.forEach(doc => {
          formsData[doc.id] = doc.data();
        });
        setRegistrationForms(formsData);
      } catch (err) {
        console.warn("Could not load registration forms:", err);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error loading payment data:', error);
      setLoading(false);
    }
  };

  // Calculate amount owed by each player with super sub support
  const calculatePlayerOwed = (playerId) => {
    let totalOwed = 0;
    let totalPaid = 0;
    const expenseBreakdown = [];

    // Find the player object to get both id, userId, and email
    const player = players.find(p => p.id === playerId || p.userId === playerId);
    
    const matchedPlayerIds = new Set();
    const matchedPlayerEmails = new Set();

    if (player) {
      matchedPlayerIds.add(player.id);
      if (player.userId) matchedPlayerIds.add(player.userId);
      if (player.email) matchedPlayerEmails.add(player.email.toLowerCase());

      const pName = player.name || '';

      teams.forEach(team => {
        (team.players || []).forEach(p => {
          const tpId = p.id || p.userId;
          const tpName = p.name || '';
          const tpEmail = p.email || '';

          const matchesUid = tpId === player.id || (player.userId && tpId === player.userId);
          const matchesEmail = tpEmail && (
            tpEmail.toLowerCase() === player.email?.toLowerCase()
          );
          const matchesName = tpName && pName && (
            tpName.toLowerCase().trim() === pName.toLowerCase().trim()
          );

          if (matchesUid || matchesEmail || matchesName) {
            if (p.id) matchedPlayerIds.add(p.id);
            if (p.userId) matchedPlayerIds.add(p.userId);
            if (p.email) matchedPlayerEmails.add(p.email.toLowerCase());
          }
        });
      });
    } else {
      matchedPlayerIds.add(playerId);
    }

    const playerIds = Array.from(matchedPlayerIds);
    const playerEmail = player?.email?.toLowerCase();
    const playerEmails = Array.from(matchedPlayerEmails);

    console.log('🔍 Calculating for player:', playerId, 'Email:', playerEmail, 'Linked IDs:', playerIds, 'Linked Emails:', playerEmails);

    // FIRST: Add Initial Donations (not tied to games) - check by ID or email
    // FIRST: Add Initial Donations (not tied to games) - check by ID or email
    const allRegistrationFees = expenses.filter(e =>
      (e.isRegistrationFee || e.isInitialDonation) && // ✅ Fix: Check both flags
      e.approvalStatus === 'approved' && // ✅ Fix: Only count approved fees
      (
        (e.splitAmong && e.splitAmong.some(id => playerIds.includes(id))) ||
        (e.playerEmail && playerEmails.includes(e.playerEmail.toLowerCase()))
      )
    );

    // DEDUPLICATE: If the database accidentally has multiple initial donations for the same player, only count ONE
    const registrationFees = allRegistrationFees.length > 0 ? [allRegistrationFees[0]] : [];

    console.log('💰 Initial Donations found:', allRegistrationFees.length, ' Deduplicated to:', registrationFees.length);

    registrationFees.forEach(fee => {
      // Use amountPerPlayer if available (for bulk donations), otherwise amount
      const amount = fee.amountPerPlayer !== undefined ? parseFloat(fee.amountPerPlayer) : parseFloat(fee.amount);

      console.log('✅ Adding Initial Donation:', amount);
      totalOwed += amount;
      expenseBreakdown.push({
        gameId: null,
        gameName: getRegistrationFeeLabel({ name: clubName }),
        gameDate: player?.registrationCompletedAt || fee.createdAt,
        expenseType: fee.description || getRegistrationFeeLabel({ name: clubName }),
        expenseDate: fee.createdAt,
        totalAmount: amount, // Use the per-player amount
        playerShare: amount,
        squadSize: 1,
        isDividable: false,
        isSuperSub: false,
        isRegistrationFee: true
      });
    });

    // SECOND: Find games where player was in playing11
    let playerGames = games.filter(game =>
      game.playing11 && game.playing11.some(id => playerIds.includes(id))
    );

    // Filter by tournament if selected
    if (selectedTournament !== 'all') {
      playerGames = playerGames.filter(game => game.tournamentId === selectedTournament);
    }

    // Calculate expenses for those games
    playerGames.forEach(game => {
      // Skip game-level expenses for unsettled tournaments — matches PlayerDashboard logic.
      // Game fees are only charged once a tournament is settled via "Settle Up".
      if (game.tournamentId) {
        const tournament = tournaments?.find(t => t.id === game.tournamentId);
        if (tournament && !tournament.isSettled) {
          return; // Skip — tournament fee will be shown separately after settlement
        }
      }

      // Exclude isTournamentFee — handled separately below via splitAmongDetails (matches player view logic)
      const gameExpenses = expenses.filter(e => e.gameId === game.id && !e.isTournamentFee && !e.isInitialDonation && e.approvalStatus === 'approved');

      // Check if player is a super sub in this game
      const isSuperSub = game.superSubs && game.superSubs.some(id => playerIds.includes(id));

      gameExpenses.forEach(expense => {
        // Use the SAME formula as PlayerDashboard.getBalanceDueBreakdown()
        // so admin balance matches what player sees exactly.
        const squadSize = game.playing11?.length || 0;
        const superSubCount = (game.superSubs?.length) || 0;
        const regularPlayerCount = squadSize - superSubCount;
        let playerShare = 0;

        if (squadSize > 0) {
          const totalShares = regularPlayerCount + (superSubCount * 0.5);
          const perShare = totalShares > 0 ? expense.amount / totalShares : 0;
          playerShare = isSuperSub ? perShare * 0.5 : perShare;
        }

        const roundedShare = Math.round(playerShare * 100) / 100;
        totalOwed += roundedShare;

        expenseBreakdown.push({
          gameId: game.id,
          gameName: game.name,
          gameDate: game.date,
          expenseType: expense.type || expense.description,
          expenseDate: expense.date,
          totalAmount: expense.amount,
          playerShare: roundedShare,
          squadSize: squadSize,
          isDividable: expense.isDividable || false,
          isSuperSub: isSuperSub,
          isRegistrationFee: false
        });
      });

      // LATE FEES: stored in game.lateArrivals (not in expenses collection)
      // Must be included here so totalOwed matches what the player was actually charged.
      const playerWasLate = playerIds.some(pid => game.lateArrivals?.[pid] === true);
      if (playerWasLate) {
        const lateFee = game.lateFeeAmount ?? 5;
        totalOwed += lateFee;
        expenseBreakdown.push({
          gameId: game.id,
          gameName: game.name,
          gameDate: game.date,
          expenseType: 'Late Arrival Fee',
          expenseDate: game.date,
          totalAmount: lateFee,
          playerShare: lateFee,
          squadSize: 1,
          isDividable: false,
          isSuperSub: false,
          isRegistrationFee: false,
          isLateFee: true
        });
      }
    });

    // Aggregate tournament fees — uses pre-rounded splitAmongDetails amounts OR settled tournaments
    const tournamentWideExpenses = expenses.filter(e =>
      (e.isTournamentFee || !e.gameId || e.level === 'tournament') &&
      e.tournamentId &&
      e.approvalStatus === 'approved'
    );

    const activeTournamentsForAdmin = selectedTournament === 'all'
      ? tournaments
      : tournaments?.filter(t => t.id === selectedTournament) || [];

    activeTournamentsForAdmin.forEach(t => {
      const tExpenses = tournamentWideExpenses.filter(e => e.tournamentId === t.id);

      tExpenses.forEach(fee => {
        if (fee.settled && fee.splitAmongDetails?.length > 0) {
          const playerShare = fee.splitAmongDetails.find(p =>
            playerIds.includes(p.playerId) ||
            playerIds.includes(p.oderId) ||
            (p.email && playerEmails.includes(p.email.toLowerCase())) ||
            (p.name && player?.name && p.name.toLowerCase().trim() === player.name.toLowerCase().trim())
          );
          if (playerShare && playerShare.gamesPlayed > 0) {
            const amount = playerShare.amount || 0;
            const perGameShare = amount / playerShare.gamesPlayed;
            const roundedShare = Math.round(perGameShare * 100) / 100;

            // Find valid games player participated in for this tournament
            const tournamentGames = games.filter(g => g.tournamentId === t.id);
            const pGames = tournamentGames.filter(g => g.playing11?.some(id => playerIds.includes(id)));

            pGames.forEach(g => {
              totalOwed += roundedShare;
              expenseBreakdown.push({
                gameId: g.id,
                gameName: g.name || `${g.homeTeam} vs ${g.awayTeam}`,
                gameDate: fee.settledAt || fee.createdAt,
                expenseType: 'Tournament Expenses share',
                expenseDate: fee.settledAt || fee.createdAt,
                totalAmount: fee.amount,
                playerShare: roundedShare,
                squadSize: fee.splitAmongDetails.length,
                isDividable: true,
                isSuperSub: false,
                isRegistrationFee: false,
                isTournamentFee: true
              });
            });
          }
        } else if (t.isSettled) {
          // Compute dynamically if tournament is settled (mirroring TournamentDetailView)
          const tournamentGames = games.filter(g => g.tournamentId === t.id);
          const validGames = tournamentGames.filter(g => g.playing11 && g.playing11.length > 0);
          const numGames = validGames.length;

          if (numGames > 0) {
            const expensePerGame = fee.amount / numGames;

            validGames.forEach(g => {
              if (g.playing11 && g.playing11.some(id => playerIds.includes(id))) {
                const squadSize = g.playing11.length;
                const superSubCount = g.superSubs?.length || 0;
                const regularCount = squadSize - superSubCount;
                const totalShares = regularCount + (superSubCount * 0.5);

                if (totalShares > 0) {
                  const isSuperSub = g.superSubs && g.superSubs.some(id => playerIds.includes(id));
                  const share = isSuperSub
                    ? (expensePerGame / totalShares) * 0.5
                    : expensePerGame / totalShares;
                  
                  const roundedShare = Math.round(share * 100) / 100;
                  totalOwed += roundedShare;
                  expenseBreakdown.push({
                    gameId: g.id,
                    gameName: g.name || `${g.homeTeam} vs ${g.awayTeam}`,
                    gameDate: fee.createdAt,
                    expenseType: 'Tournament Expenses share',
                    expenseDate: fee.createdAt,
                    totalAmount: fee.amount,
                    playerShare: roundedShare,
                    squadSize: squadSize,
                    isDividable: true,
                    isSuperSub: isSuperSub,
                    isRegistrationFee: false,
                    isTournamentFee: true
                  });
                }
              }
            });
          }
        }
      });
    });

    // SINGLE SOURCE OF TRUTH: walletTransactions with type='payment' or 'waiver'
    // Negative amounts = money received from player OR waived amount
    const playerWalletTransactions = walletTransactions.filter(t =>
      (t.type === 'payment' || t.type === 'waiver') &&
      t.status === 'approved' &&
      t.amount < 0 &&
      !t.isDonation && // Exclude donations from calculations
      (
        playerIds.includes(t.userId) ||
        (t.playerEmail && playerEmails.includes(t.playerEmail.toLowerCase())) ||
        (player?.name && t.playerName?.toLowerCase().trim() === player.name.toLowerCase().trim())
      )
    );

    // Calculate Paid and Waived separately
    totalPaid = playerWalletTransactions
      .filter(t => t.type === 'payment')
      .reduce((sum, t) => sum + Math.abs(t.amount), 0);

    const totalWaived = playerWalletTransactions
      .filter(t => t.type === 'waiver')
      .reduce((sum, t) => sum + Math.abs(t.amount), 0);

    // Calculate Donations separately
    const totalDonated = walletTransactions
      .filter(t => t.type === 'payment' && t.status === 'approved' && t.amount < 0 && t.isDonation && (playerIds.includes(t.userId) || (t.playerEmail && playerEmails.includes(t.playerEmail.toLowerCase())) || (player?.name && t.playerName?.toLowerCase().trim() === player.name.toLowerCase().trim())))
      .reduce((sum, t) => sum + Math.abs(t.amount || 0), 0);
    // Calculate Net Reimbursements (Club Owes Player)
    const netReimbursementOwed = walletTransactions
      .filter(t => 
        t.status === 'approved' && 
        (t.type === 'expense_reimbursement' || t.type === 'reimbursement_paid') &&
        (playerIds.includes(t.userId) || (t.playerEmail && playerEmails.includes(t.playerEmail.toLowerCase())) || (player?.name && t.playerName?.toLowerCase().trim() === player.name.toLowerCase().trim()))
      )
      .reduce((sum, t) => sum + (t.amount || 0), 0);

    // Round to cents to eliminate floating-point accumulation from per-game division
    totalOwed = Math.round(totalOwed * 100) / 100;
    totalPaid = Math.round(totalPaid * 100) / 100;

    const rawBalance = totalOwed - totalPaid - totalWaived - netReimbursementOwed;
    const balance = Math.round(rawBalance * 100) / 100;

    return {
      totalOwed,
      totalPaid,
      totalWaived,
      totalDonated, // ✅ Include donations
      balance,
      gamesPlayed: playerGames.length,
      expenseBreakdown
    };
  };


  // Get detailed breakdown for a player
  const getPlayerExpenseDetails = (playerId) => {
    const { expenseBreakdown } = calculatePlayerOwed(playerId);

    // Group by game (use 'registration' as key for Initial Donations)
    const byGame = {};
    expenseBreakdown.forEach(item => {
      const key = item.isRegistrationFee ? 'registration' : item.gameId;

      if (!byGame[key]) {
        byGame[key] = {
          gameName: item.gameName,
          gameDate: item.gameDate,
          expenses: [],
          total: 0,
          isRegistrationFee: item.isRegistrationFee || false
        };
      }
      byGame[key].expenses.push(item);
      byGame[key].total += item.playerShare;
    });

    return byGame;
  };

  // Mark payment as paid
  const handleMarkAsPaid = async (playerId, amount, note = '', method = 'admin_recorded') => {
    try {
      const player = players.find(p => p.id === playerId);

      // SINGLE SOURCE OF TRUTH: Create wallet transaction
      const walletTransaction = {
        userId: playerId,
        clubId: clubId,
        type: 'payment',
        amount: -parseFloat(amount), // Negative = reduces what player owes
        description: note || 'Payment recorded by admin',
        playerName: player.name,
        playerEmail: player.email,
        paymentMethod: method,
        status: 'approved',
        createdAt: new Date().toISOString(),
        recordedBy: 'admin',
        note: note // redundancy for clarity
      };

      await addDoc(collection(db, 'walletTransactions'), walletTransaction);

      // TELEMETRY: Log payment recorded
      if (telemetry) {
        telemetry.logEvent('payment_recorded', {
          amount: parseFloat(amount),
          method: method,
          recordedBy: 'admin'
        });
      }

      alert('Payment recorded successfully!');
      await loadData();
    } catch (error) {
      console.error('Error recording payment:', error);
      alert('Failed to record payment. Please try again.');
    }
  };

  // Record partial payment - Opens Modal
  const handlePartialPayment = (playerId) => {
    const player = players.find(p => p.id === playerId);
    if (!player) return;

    setPaymentTarget(player);
    setPaymentAmount('');
    setPaymentMethod('Zelle');
    setPaymentNote('');
    setShowPaymentModal(true);
  };

  const submitPayment = async () => {
    if (!paymentTarget) return;

    const amount = parseFloat(paymentAmount);
    if (isNaN(amount) || amount <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    if (!paymentNote.trim()) {
      alert('Please provide a note for this payment');
      return;
    }

    await handleMarkAsPaid(paymentTarget.id, amount, paymentNote.trim(), paymentMethod);

    // Close modal
    setShowPaymentModal(false);
    setPaymentTarget(null);
  };

  // Mark full balance as paid - Opens Modal
  const handlePayFull = (playerId) => {
    const { balance } = calculatePlayerOwed(playerId);

    if (balance <= 0) {
      alert('Player has no balance due');
      return;
    }

    const player = players.find(p => p.id === playerId);
    if (!player) return;

    setPaymentTarget(player);
    setPaymentAmount(balance.toFixed(2));
    setPaymentMethod('Zelle');
    setPaymentNote('');
    setShowPaymentModal(true);
  };

  // Waive Initial Donation for a player - Opens Modal
  const handleWaiveFee = (playerId) => {
    const player = players.find(p => p.id === playerId);
    if (!player) return;

    // Find the Initial Donation for this player
    const registrationFee = expenses.find(e =>
      (e.isRegistrationFee || e.isInitialDonation) &&
      e.approvalStatus === 'approved' &&
      (e.splitAmong?.includes(playerId) ||
        e.splitAmong?.includes(player.userId) ||
        e.playerEmail?.toLowerCase() === player.email?.toLowerCase())
    );

    if (!registrationFee) {
      alert(`No ${getRegistrationFeeLabel({ name: clubName })} found for this player`);
      return;
    }

    setWaiverTarget({ player, fee: registrationFee });
    setWaiverType('full');
    setWaiverAmount(registrationFee.amount.toString());
    setWaiverReason('');
    setShowWaiverModal(true);
  };

  // Record a Reimbursement payout - Opens Modal
  const handleSettleReimbursement = (playerId, amount) => {
    const player = players.find(p => p.id === playerId);
    if (!player) return;

    setReimbursementTarget({ player, defaultAmount: amount });
    setReimbursementAmount(amount.toFixed(2));
    setReimbursementNote('');
    setShowReimbursementModal(true);
  };

  const submitReimbursement = async () => {
    if (!reimbursementTarget) return;

    const amount = parseFloat(reimbursementAmount);
    if (isNaN(amount) || amount <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    if (!reimbursementNote.trim()) {
      alert('Please provide a note for this settlement');
      return;
    }

    try {
      await addDoc(collection(db, 'walletTransactions'), {
        userId: reimbursementTarget.player.id,
        clubId: clubId,
        type: 'reimbursement_paid',
        amount: -amount, // Negative because club pays out to clear the debt
        description: `Reimbursement settled: ${reimbursementNote.trim()}`,
        status: 'approved',
        createdAt: new Date().toISOString(),
        recordedBy: 'admin'
      });
      alert('Reimbursement settlement recorded successfully!');
      
      setShowReimbursementModal(false);
      setReimbursementTarget(null);
      
      await loadData();
    } catch (error) {
      console.error('Error settling reimbursement:', error);
      alert('Failed to settle reimbursement. Please try again.');
    }
  };

  const submitWaiver = async () => {
    if (!waiverTarget) return;

    try {
      const amount = waiverType === 'full'
        ? waiverTarget.fee.amount
        : parseFloat(waiverAmount);

      if (isNaN(amount) || amount <= 0) {
        alert('Please enter a valid amount');
        return;
      }

      if (amount > waiverTarget.fee.amount) {
        alert(`Cannot waive more than the fee amount ($${waiverTarget.fee.amount})`);
        return;
      }

      if (!waiverReason.trim()) {
        alert('Please provide a reason for the waiver');
        return;
      }

      // Create a waiver transaction that offsets the Initial Donation
      const waiverTransaction = {
        userId: waiverTarget.player.id,
        clubId: clubId,
        type: 'waiver',
        amount: -amount, // Negative = reduces what player owes
        description: `${getRegistrationFeeLabel({ name: clubName })} waived (${waiverType}): ${waiverReason}`,
        playerName: waiverTarget.player.name,
        playerEmail: waiverTarget.player.email,
        relatedExpenseId: waiverTarget.fee.id,
        status: 'approved',
        createdAt: new Date().toISOString(),
        waivedBy: user?.name || user?.displayName || user?.email || 'admin',
        waiverReason: waiverReason
      };

      await addDoc(collection(db, 'walletTransactions'), waiverTransaction);

      // Close modal and reset
      setShowWaiverModal(false);
      setWaiverTarget(null);

      alert(`Waived ${formatCurrency(amount)} for ${waiverTarget.player.name}`);
      await loadData();
    } catch (error) {
      console.error('Error waiving fee:', error);
      alert('Failed to waive fee. Please try again.');
    }
  };

  // Check if player has a Initial Donation that can be waived
  const canWaiveFee = (playerId) => {
    const player = players.find(p => p.id === playerId);
    if (!player) return false;

    // Check if player has a Initial Donation
    const hasRegFee = expenses.some(e =>
      (e.isRegistrationFee || e.isInitialDonation) &&
      e.approvalStatus === 'approved' &&
      (e.splitAmong?.includes(playerId) ||
        e.splitAmong?.includes(player.userId) ||
        e.playerEmail?.toLowerCase() === player.email?.toLowerCase())
    );

    if (!hasRegFee) return false;

    // Check if fee is already waived
    const alreadyWaived = walletTransactions.some(t =>
      t.type === 'waiver' &&
      (t.userId === playerId || t.userId === player.userId)
    );

    return !alreadyWaived;
  };

  // Check if player has a Initial Donation that can be updated (independent of waivers)
  const canUpdateFee = (playerId) => {
    const player = players.find(p => p.id === playerId);
    if (!player) return false;

    // Check if player has a Initial Donation
    return expenses.some(e =>
      (e.isRegistrationFee || e.isInitialDonation) &&
      e.approvalStatus === 'approved' &&
      (e.splitAmong?.includes(playerId) ||
        e.splitAmong?.includes(player.userId) ||
        e.playerEmail?.toLowerCase() === player.email?.toLowerCase())
    );
  };

  // Update Fee Handler - Opens Modal
  const handleUpdateFee = (playerId) => {
    const player = players.find(p => p.id === playerId);
    if (!player) return;

    // Calculate CURRENT TOTAL owed for Registration/Initial Donation from ALL related expenses
    // This allows us to support "Adjustment" logic correctly
    const relevantExpenses = expenses.filter(e =>
      (e.isInitialDonation || e.isRegistrationFee) &&
      e.approvalStatus === 'approved' &&
      (e.splitAmong?.includes(playerId) ||
        e.splitAmong?.includes(player.userId) ||
        (e.playerEmail && player.email && e.playerEmail.toLowerCase() === player.email.toLowerCase()))
    );

    if (relevantExpenses.length === 0) {
      alert(`No ${getRegistrationFeeLabel({ name: clubName })} found for this player.`);
      return;
    }

    const currentTotal = relevantExpenses.reduce((sum, e) => {
      const val = e.amountPerPlayer !== undefined ? parseFloat(e.amountPerPlayer) : parseFloat(e.amount);
      return sum + (val || 0);
    }, 0);

    setUpdateFeeTarget({
      player,
      currentTotal, // Store the calculated total
      relevantExpenses // Keep reference if needed
    });
    setUpdateFeeAmount(currentTotal.toString());
    setUpdateFeeReason('');
    setShowUpdateFeeModal(true);
  };

  // Submit Edit Note
  const submitEditNote = async () => {
    if (!editNoteTarget) return;

    try {
      const docRef = doc(db, 'walletTransactions', editNoteTarget.id);
      const updates = {};

      if (editNoteTarget.type === 'waiver') {
        updates.waiverReason = editNoteText;
        
        // Rebuild description and replace old terminology
        let oldDesc = editNoteTarget.description || '';
        oldDesc = oldDesc.replace('Initial Donation waived', `${getRegistrationFeeLabel({ name: clubName })} waived`);
        
        // Replace the reason part (everything after colon)
        if (oldDesc.includes(': ')) {
          updates.description = oldDesc.substring(0, oldDesc.lastIndexOf(': ') + 2) + editNoteText;
        } else {
          updates.description = `${getRegistrationFeeLabel({ name: clubName })} waived: ${editNoteText}`;
        }
      } else if (editNoteTarget.type === 'payment') {
        updates.paymentNote = editNoteText;
        updates.description = editNoteText;
        // Save tournament association for payment transactions
        const selectedTournament = tournaments?.find(t => t.id === editNoteTournamentId);
        updates.tournamentId = editNoteTournamentId || null;
        updates.tournamentName = selectedTournament?.name || null;
        // If associated with a tournament, it cannot be a donation. Otherwise, preserve original donation status.
        updates.isDonation = editNoteTournamentId ? false : (editNoteTarget.isDonation || false);
      } else {
        updates.description = editNoteText;
      }

      await updateDoc(docRef, updates);

      setShowEditNoteModal(false);
      setEditNoteTarget(null);
      setEditNoteText('');
      setEditNoteTournamentId('');
      
      alert('Payment updated successfully');
      await loadData();
    } catch (err) {
      console.error('Error updating payment:', err);
      alert('Failed to update payment.');
    }
  };

  const submitUpdateFee = async () => {
    if (!updateFeeTarget || !updateFeeAmount || !updateFeeReason) return;

    // Calculate adjustment amount (New Target - Current Total)
    // Example: Current Total $250. New Target $125. Adjustment = -125.
    const targetAmount = parseFloat(updateFeeAmount);
    if (isNaN(targetAmount)) {
      alert("Please enter a valid amount.");
      return;
    }

    const adjustmentAmount = targetAmount - updateFeeTarget.currentTotal;

    if (Math.abs(adjustmentAmount) < 0.01) {
      alert("New amount is the same as current total. No update needed.");
      return;
    }

    try {
      console.log(`🔵 Adjusting fee for ${updateFeeTarget.player.name}`);
      console.log(`Current: ${updateFeeTarget.currentTotal}, Target: ${targetAmount}, Adjustment: ${adjustmentAmount}`);

      const adjustmentData = {
        description: `Corrected ${getRegistrationFeeLabel({ name: clubName })}: ${updateFeeReason}`,
        amount: adjustmentAmount, // Can be negative
        amountPerPlayer: adjustmentAmount,
        currency: 'USD', // Default
        date: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        createdBy: user.email || 'admin',
        clubId: clubId,
        splitAmong: [updateFeeTarget.player.id], // Assign strictly to this player
        playerEmail: updateFeeTarget.player.email,
        isRegistrationFee: true, // Mark as registration related so it appears in the right section
        isInitialDonation: false, // NOT an initial donation, but an adjustment
        type: 'Adjustment',
        approvalStatus: 'approved', // Auto-approve adjustments by treasurer
        approvedBy: user.uid,
        approvedAt: new Date().toISOString(),
        addedByPlayerId: user.uid // Admin/Treasurer ID
      };

      await addDoc(collection(db, 'expenses'), adjustmentData);

      // Log telemetry if available
      if (window.telemetry) {
        window.telemetry.logEvent('initial_donation_adjusted', {
          clubId: clubId,
          playerId: updateFeeTarget.player.id,
          oldTotal: updateFeeTarget.currentTotal,
          newTarget: targetAmount,
          adjustment: adjustmentAmount
        });
      }

      setShowUpdateFeeModal(false);
      setUpdateFeeTarget(null);
      setUpdateFeeAmount('');
      setUpdateFeeReason('');

      // Reload is handled by real-time listeners, but we can force simpler UI refresh if needed
      if (typeof loadData === 'function') loadData(); // Call loadData if available
      alert(`Fee adjusted successfully. Added adjustment of ${formatCurrency(adjustmentAmount)}.`);

    } catch (error) {
      console.error('Error updating fee:', error);
      alert('Failed to update fee: ' + error.message);
    }
  };

  // Get payment status for a player
  const getPaymentStatus = (playerId) => {
    const { totalOwed, balance } = calculatePlayerOwed(playerId);

    if (totalOwed === 0) return 'none';
    if (balance <= 0.01) return 'paid'; // Allow small rounding differences
    if (balance < totalOwed) return 'partial';
    return 'pending';
  };

  // Helper to get the correct registration/payment status badge
  const getRegistrationStatusBadge = (player, balance) => {
    // Check for approved Initial Donation payment (Implied Approval)
    const hasApprovedInitialPayment = paymentSubmissions.some(sub =>
      (sub.playerId === player.id || sub.userId === player.id) &&
      sub.status === 'approved' &&
      sub.notes &&
      sub.notes.toLowerCase().includes('initial donation')
    );

    // Get Registration Form Data (Source of Truth)
    const regForm = registrationForms[player.id];

    // Use regForm status OR player status OR implied status
    const status = regForm?.status || player.registrationStatus || (hasApprovedInitialPayment ? 'approved' : null);

    // 1. Signed-up: No registration data
    if (!status) {
      return <div className="w-3 h-3 rounded-full shadow-sm bg-yellow-400" title="Signed-up" />;
    }

    // 2. Awaiting Registration: Submitted but not approved
    if (status === 'pending') {
      return <div className="w-3 h-3 rounded-full shadow-sm bg-yellow-400" title="Awaiting Registration" />;
    }

    // 3. Registered: Approved (or Completed)
    if (status === 'approved' || status === 'completed') {
      if (balance <= 0.01) { // Paid
        return <div className="w-3 h-3 rounded-full shadow-sm bg-green-700" title="Registered Approved" />;
      } else { // Unpaid
        return <div className="w-3 h-3 rounded-full shadow-sm bg-green-400" title="Registered Pending" />;
      }
    }

    return <div className="w-3 h-3 rounded-full shadow-sm bg-yellow-400" title="Signed-up" />;
  };

  // Filter players
  const getFilteredPlayers = () => {
    return players.filter(player => {


      // 2. Member Name Filter (Multi-select)
      if (selectedPlayers.length > 0 && !selectedPlayers.includes(player.id)) {
        return false;
      }

      // 3. Status Filter (Registration Status) & Sub Status
      const { balance } = calculatePlayerOwed(player.id); // Calculate balance for accurate status
      const badgeElement = getRegistrationStatusBadge(player, balance); // Pass balance!
      let badgeStatus = 'Signed-up'; // Default
      let subStatus = 'Standard';

      if (badgeElement) {
        const title = badgeElement.props.title;
        if (title === 'Registered Approved') badgeStatus = 'registered_approved';
        else if (title === 'Registered Pending') badgeStatus = 'registered_pending';
        else if (title === 'Signed-up') badgeStatus = 'signed_up';
        else if (title === 'Awaiting Registration') badgeStatus = 'awaiting_registration';
      }

      // Determine sub-status (e.g. Awaited/Benevity) logic
      const pendingSubmissions = paymentSubmissions.filter(s =>
        s.status === 'pending' &&
        (s.playerId === player.id || (player.userId && s.playerId === player.userId))
      );

      // Check for Awaited status in pending submissions
      const hasAwaitedPayment = pendingSubmissions.some(sub =>
        sub.subStatus === 'Awaited' ||
        (sub.paymentMethod && (
          sub.paymentMethod.toLowerCase().includes('benevity') ||
          sub.paymentMethod.toLowerCase().includes('workday')
        ))
      );

      if (hasAwaitedPayment) {
        subStatus = 'Awaited';
      }

      if (statusFilter !== 'all') {
        if (statusFilter === 'signed_up' && badgeStatus !== 'signed_up') return false;
        if (statusFilter === 'registered' && (badgeStatus !== 'registered_pending' && badgeStatus !== 'registered_approved')) return false;
        if (statusFilter === 'registered_approved' && badgeStatus !== 'registered_approved') return false;
        if (statusFilter === 'registered_pending' && badgeStatus !== 'registered_pending') return false;
        if (statusFilter === 'awaiting' && badgeStatus !== 'awaiting_registration') return false;
      }



      // 4. Payment to Review Filter
      if (showReviewOnly) {
        const pendingSubmissions = paymentSubmissions.filter(s =>
          s.status === 'pending' &&
          (s.playerId === player.id || (player.userId && s.playerId === player.userId))
        );
        if (pendingSubmissions.length === 0) return false;
      }

      return true;
    }).sort((a, b) => a.name.localeCompare(b.name));
  };

  // Helper for dropdown
  const filteredPlayersForDropdown = players
    .filter(p => p.name.toLowerCase().includes(playerSearchTerm.toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name));

  const togglePlayerSelection = (playerId) => {
    if (selectedPlayers.includes(playerId)) {
      setSelectedPlayers(prev => prev.filter(id => id !== playerId));
    } else {
      setSelectedPlayers(prev => [...prev, playerId]);
    }
  };

  const toggleSelectAllPlayers = () => {
    if (selectedPlayers.length === filteredPlayersForDropdown.length) {
      setSelectedPlayers([]);
    } else {
      setSelectedPlayers(filteredPlayersForDropdown.map(p => p.id));
    }
  };

  // Calculate summary statistics
  const getSummaryStats = (playerList) => {
    let totalOwed = 0;
    let totalPaid = 0;
    let totalWaived = 0;
    let totalDonated = 0;

    playerList.forEach(player => {
      const stats = calculatePlayerOwed(player.id);
      totalOwed += stats.totalOwed;
      totalPaid += stats.totalPaid;
      totalWaived += stats.totalWaived;
      totalDonated += stats.totalDonated;
    });

    // Total Pending excluding waivers (money actually still to be collected)
    const totalPending = Math.max(0, totalOwed - totalPaid - totalWaived);

    // Collection Rate based on Collectable Amount (Total Owed - Waived)
    const collectableAmount = totalOwed - totalWaived;
    const collectionRate = collectableAmount > 0 ? (totalPaid / collectableAmount) * 100 : 0;

    return {
      totalOwed,
      totalPaid,
      totalWaived,
      totalDonated,
      totalPending,
      collectionRate
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  const filteredPlayers = getFilteredPlayers();
  const stats = getSummaryStats(filteredPlayers);

  return (
    <div className="max-w-7xl mx-auto">
      <button onClick={onBack} className="text-gray-600 mb-4 hover:text-gray-900">
        ← Back
      </button>

      <div className="mb-8">
        <h2 className="text-3xl font-bold mb-2">Manage Payments</h2>
        <p className="text-gray-600">Track and manage player payments</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8"> {/* Changed to 5 columns */}
        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex items-center justify-between mb-2">
            <DollarSign className="text-orange-600" size={24} />
          </div>
          <p className="text-sm text-gray-500 mb-1">Total Owed</p>
          <p className="text-2xl font-bold">{formatCurrency(stats.totalOwed)}</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex items-center justify-between mb-2">
            <CheckCircle className="text-green-600" size={24} />
          </div>
          <p className="text-sm text-gray-500 mb-1">Total Paid</p>
          <p className="text-2xl font-bold text-green-600">{formatCurrency(stats.totalPaid)}</p>
        </div>

        {/* New Waived Card */}
        <div className="bg-white rounded-xl shadow-sm p-4 border-2 border-purple-100">
          <div className="flex items-center justify-between mb-2">
            <Gift className="text-purple-600" size={24} />
          </div>
          <p className="text-sm text-gray-500 mb-1">Waived</p>
          <p className="text-2xl font-bold text-purple-600">{formatCurrency(stats.totalWaived)}</p>
        </div>

        {/* New Additional Donations Card */}
        <div className="bg-white rounded-xl shadow-sm p-4 border-2 border-pink-100">
          <div className="flex items-center justify-between mb-2">
            <Heart className="text-pink-600" size={24} />
          </div>
          <p className="text-sm text-gray-500 mb-1">Additional Donations</p>
          <p className="text-2xl font-bold text-pink-600">{formatCurrency(stats.totalDonated)}</p>
        </div>

        <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl shadow-sm p-4 border-2 border-orange-200">
          <div className="flex items-center justify-between mb-2">
            <Users className="text-orange-600" size={24} />
          </div>
          <p className="text-sm font-medium text-orange-900 mb-1">
            Receivables <span className="text-xs font-normal text-orange-700">(Pending + Partial)</span>
          </p>
          <p className="text-2xl font-bold text-orange-700">{formatCurrency(stats.totalPending)}</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex items-center justify-between mb-2">
            <AlertCircle className="text-blue-600" size={24} />
          </div>
          <p className="text-sm text-gray-500 mb-1">Collection Rate</p>
          <p className="text-2xl font-bold text-blue-600">{stats.collectionRate.toFixed(1)}%</p>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
        <div className="flex flex-col gap-4">

          <div className="flex flex-wrap items-center gap-4">
            {/* Member Multi-Select */}
            <div className="relative min-w-[250px]" ref={dropdownRef}>
              <div
                className="flex items-center justify-between w-full p-2 bg-white border rounded-lg cursor-pointer hover:border-blue-500"
                onClick={() => setIsPlayerDropdownOpen(!isPlayerDropdownOpen)}
              >
                <span className="truncate">
                  {selectedPlayers.length === 0
                    ? 'Filter by Members'
                    : `${selectedPlayers.length} Members Selected`}
                </span>
                <Filter size={16} className="text-gray-500" />
              </div>

              {isPlayerDropdownOpen && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-hidden flex flex-col">
                  <div className="p-2 border-b">
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                      <input
                        type="text"
                        placeholder="Search members..."
                        value={playerSearchTerm}
                        onChange={(e) => setPlayerSearchTerm(e.target.value)}
                        className="w-full pl-8 pr-2 py-1 text-sm border rounded outline-none focus:ring-1 focus:ring-blue-500"
                        autoFocus
                      />
                    </div>
                  </div>
                  <div className="p-2 border-b bg-gray-50 flex justify-between">
                    <button
                      onClick={toggleSelectAllPlayers}
                      className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                    >
                      {selectedPlayers.length === filteredPlayersForDropdown.length ? 'Deselect All' : 'Select All'}
                    </button>
                    <span className="text-xs text-gray-500">{filteredPlayersForDropdown.length} found</span>
                  </div>
                  <div className="overflow-y-auto flex-1 p-1">
                    {filteredPlayersForDropdown.map(player => (
                      <div
                        key={player.id}
                        onClick={() => togglePlayerSelection(player.id)}
                        className="flex items-center gap-2 p-2 hover:bg-blue-50 cursor-pointer rounded text-sm"
                      >
                        <div className={`w-4 h-4 border rounded flex items-center justify-center 
                                        ${selectedPlayers.includes(player.id) ? 'bg-blue-600 border-blue-600' : 'border-gray-300'} `}>
                          {selectedPlayers.includes(player.id) && <CheckCircle size={12} className="text-white" />}
                        </div>
                        <span className="text-gray-700">{player.name}</span>
                      </div>
                    ))}
                    {filteredPlayersForDropdown.length === 0 && (
                      <div className="p-4 text-center text-gray-400 text-sm">No members found</div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Review Only Toggle */}
            <label className={`flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-colors ${showReviewOnly ? 'bg-orange-50 border-orange-200 text-orange-700' : 'bg-white border-gray-200 text-gray-600'}`}>
              <input
                type="checkbox"
                checked={showReviewOnly}
                onChange={(e) => setShowReviewOnly(e.target.checked)}
                className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500"
              />
              <span className="font-medium">Payments to Review</span>
              {paymentSubmissions.filter(s => s.status === 'pending').length > 0 && (
                <span className="bg-orange-100 text-orange-800 text-xs px-2 py-0.5 rounded-full font-bold">
                  {paymentSubmissions.filter(s => s.status === 'pending').length}
                </span>
              )}
            </label>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 min-w-[150px]"
            >
              <option value="all">All Statuses</option>
              <option value="signed_up">Signed Up</option>
              <option value="registered_pending">Registered (Pending)</option>
              <option value="registered_approved">Registered (Approved)</option>
              <option value="awaiting">Awaiting Registration</option>
            </select>



            {/* Clear Filters */}
            {(selectedPlayers.length > 0 || showReviewOnly || statusFilter !== 'all') && (
              <button
                onClick={() => {
                  setSelectedPlayers([]);
                  setShowReviewOnly(false);
                  setStatusFilter('all');
                }}
                className="text-red-600 hover:text-red-800 text-sm font-medium flex items-center gap-1 ml-auto"
              >
                <AlertCircle size={14} /> Clear Filters
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tournament Filter Notice */}
      {selectedTournament !== 'all' && (
        <div className="bg-purple-50 border-2 border-purple-300 rounded-xl p-4 mb-6">
          <div className="flex items-start gap-3">
            <div className="bg-purple-600 text-white rounded-full p-2">
              <Filter size={20} />
            </div>
            <div className="flex-1">
              <h4 className="font-bold text-purple-900 mb-1">
                Tournament Filter Active
              </h4>
              <p className="text-sm text-purple-800">
                {(() => {
                  const tournament = tournaments?.find(t => t.id === selectedTournament);
                  return tournament ? (
                    <>
                      Showing payment data for <span className="font-semibold">{tournament.name}</span> only.
                      Player balances, amounts owed, and payments are filtered to this tournament.
                    </>
                  ) : 'Showing tournament-specific payment data.';
                })()}
              </p>
            </div>
            <button
              onClick={() => setSelectedTournament('all')}
              className="text-purple-600 hover:text-purple-800 font-semibold text-sm whitespace-nowrap"
            >
              Clear Filter
            </button>
          </div>
        </div>
      )}

      {/* Players List */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-xl font-bold mb-4">
          Players ({filteredPlayers.length})
        </h3>

        {filteredPlayers.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p>No players found</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredPlayers.map(player => {
              const { totalOwed, totalPaid, totalWaived, balance, gamesPlayed } = calculatePlayerOwed(player.id);
              const status = getPaymentStatus(player.id);
              // Get payment and waiver history from walletTransactions
              const playerPaymentsList = walletTransactions.filter(t =>
                (t.type === 'payment' || t.type === 'waiver' || t.type === 'reimbursement_paid') &&
                t.status === 'approved' &&
                t.amount < 0 &&
                (t.userId === player.id || t.userId === player.userId)
              );
              const expenseDetails = getPlayerExpenseDetails(player.id);

              return (
                <div key={player.id} className="p-4 border rounded-lg hover:border-green-300 transition-colors">
                  <div className="flex items-start justify-between">
                    {/* Player Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-lg">{player.name}</h4>

                        {/* New Registration Status Badge */}
                        {getRegistrationStatusBadge(player, balance)}
                      </div>

                      <p className="text-sm text-gray-500 mt-1">
                        Team: {player.teamName} • Games: {gamesPlayed}
                      </p>
                    </div>

                    {/* Payment Stats */}
                    <div className="text-right mr-4">
                      <p className="text-sm text-gray-500">{balance < -0.01 ? 'Club Owes' : 'Balance Due'}</p>
                      <p className={`text-lg font-bold ${balance < -0.01 ? 'text-blue-600' : balance <= 0.01 ? 'text-green-600' : 'text-gray-900'}`}>
                        {formatCurrency(Math.abs(balance))}
                      </p>
                      {balance >= -0.01 && (
                        <p className="text-xs text-gray-400 mt-1">
                          Paid: {formatCurrency(totalPaid)} of {formatCurrency(totalOwed)}
                        </p>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-2 items-end w-full sm:w-auto mt-2 sm:mt-0">
                      {!(isTreasurer || isAdmin) && Math.abs(balance) > 0.01 && (
                        <div className="text-xs text-gray-500 italic text-right px-2">
                          Read Only
                        </div>
                      )}

                      {(isTreasurer || isAdmin) && player.id === currentUserId && Math.abs(balance) > 0.01 && (
                        <div className="text-xs text-orange-600 italic text-right px-2">
                          Cannot update own payments
                        </div>
                      )}

                      {(isTreasurer || isAdmin) && player.id !== currentUserId && (
                        <>
                          {balance < -0.01 ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSettleReimbursement(player.id, Math.abs(balance));
                              }}
                              className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition"
                            >
                              Settle Club Debt
                            </button>
                          ) : (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePartialPayment(player.id);
                              }}
                              className="px-3 py-1.5 bg-green-600 text-white rounded text-sm hover:bg-green-700 transition"
                            >
                              Record Payment
                            </button>
                          )}

                          {/* Waive - Only if eligible */}
                          {balance > 0.01 && canWaiveFee(player.id) && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleWaiveFee(player.id);
                              }}
                              className="px-3 py-1.5 bg-purple-600 text-white rounded text-sm hover:bg-purple-700 transition"
                            >
                              Waive
                            </button>
                          )}

                          {/* Review - Pending Payment Submissions exist */}
                          {(() => {
                            const pendingSubmissions = paymentSubmissions.filter(s =>
                              s.status === 'pending' &&
                              (s.playerId === player.id || (player.userId && s.playerId === player.userId))
                            );

                            if (pendingSubmissions.length > 0) {
                              return (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setReviewTarget({
                                      player,
                                      submissions: pendingSubmissions
                                    });
                                    setShowReviewModal(true);
                                  }}
                                  className="px-3 py-1.5 bg-orange-500 text-white rounded text-sm hover:bg-orange-600 transition flex items-center gap-1"
                                >
                                  <AlertCircle size={14} />
                                  Review ({pendingSubmissions.length})
                                </button>
                              );
                            }
                            return null;
                          })()}
                        </>
                      )}

                      {/* View Details - everyone can see details */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedPlayer(selectedPlayer?.id === player.id ? null : player);
                        }}
                        className="px-3 py-1.5 text-gray-600 hover:bg-gray-100 rounded text-sm transition border border-gray-300 w-full sm:w-auto"
                      >
                        {selectedPlayer?.id === player.id ? 'Hide Details' : 'View Details'}
                      </button>
                    </div>
                  </div>

                  {/* Detailed Breakdown (if selected) */}
                  {selectedPlayer?.id === player.id && (
                    <div className="mt-4 pt-4 border-t space-y-4">
                      {/* Expense Breakdown by Game */}
                      {Object.keys(expenseDetails).length > 0 && (
                        <div>
                          <h5 className="font-semibold mb-3 flex items-center gap-2">
                            <DollarSign size={16} />
                            Expense Breakdown
                          </h5>
                          <div className="space-y-3">
                            {Object.entries(expenseDetails).map(([gameId, gameData]) => (
                              <div key={gameId} className="p-3 bg-gray-50 rounded-lg">
                                <div className="font-medium text-sm mb-2">
                                  {gameData.gameName} - {formatDate(gameData.gameDate)}
                                </div>
                                <div className="space-y-1">
                                  {gameData.expenses.map((expense, idx) => (
                                    <div key={idx} className="flex justify-between text-xs text-gray-600 pl-3">
                                      <span className="flex items-center gap-1">
                                        {expense.expenseType === 'Initial Donation' ? '' : expense.expenseType}
                                        {expense.isDividable && (
                                          <span className="text-blue-600">
                                            (÷{expense.squadSize})
                                          </span>
                                        )}
                                        {expense.isSuperSub && (
                                          <span className="inline-flex items-center gap-0.5 text-purple-600">
                                            <Star size={10} fill="currentColor" />
                                            Super Sub
                                          </span>
                                        )}
                                      </span>
                                      <span className="font-medium">{formatCurrency(expense.playerShare)}</span>
                                    </div>
                                  ))}
                                  <div className="flex justify-between text-sm font-semibold pt-1 border-t border-gray-200">
                                    <span>Game Total</span>
                                    <span className="text-green-600">{formatCurrency(gameData.total)}</span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Payment History */}
                      {playerPaymentsList.length > 0 && (
                        <div>
                          <h5 className="font-semibold mb-2 flex items-center gap-2">
                            <CheckCircle size={16} />
                            Payment & Waiver History
                          </h5>
                          <div className="space-y-2">
                            {playerPaymentsList.map(payment => (
                              <div
                                key={payment.id}
                                className={`group flex justify-between text-sm p-2 rounded ${
                                  payment.type === 'waiver' ? 'bg-purple-50' : 
                                  payment.type === 'reimbursement_paid' ? 'bg-blue-50' : 'bg-green-50'
                                }`}
                              >
                                <div>
                                  <span className="font-medium">{formatCurrency(Math.abs(payment.amount))}</span>
                                  <span className="text-gray-500 ml-2">
                                    {payment.paymentDate
                                      ? formatDate(payment.paymentDate)
                                      : payment.createdAt
                                        ? formatDate(payment.createdAt)
                                        : ''}
                                  </span>
                                  {payment.type === 'waiver' && payment.waiverReason && (
                                    <span className="text-gray-400 ml-2 text-xs group-hover:text-gray-600 transition-colors">
                                      ({payment.waiverReason}{payment.waivedBy && payment.waivedBy !== 'admin' && ` - by ${payment.waivedBy}`})
                                    </span>
                                  )}
                                  {payment.type === 'payment' && payment.paymentMethod && (
                                    <span className="text-gray-400 ml-2 text-xs">
                                      via {payment.paymentMethod}
                                    </span>
                                  )}
                                  {payment.type === 'payment' && payment.paymentNote && (
                                    <span className="text-gray-400 ml-2 text-xs block mt-0.5 ml-4">
                                      Note: {payment.paymentNote}
                                    </span>
                                  )}
                                  {payment.type === 'reimbursement_paid' && payment.description && (
                                    <span className="text-gray-400 ml-2 text-xs group-hover:text-gray-600 transition-colors">
                                      {payment.description.replace('Reimbursement settled by admin', 'Manual Settlement')}
                                    </span>
                                  )}
                                  
                                  {/* Edit Note Button */}
                                  {(isTreasurer || isAdmin) && (
                                    <>
                                      <button 
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setEditNoteTarget(payment);
                                          const textToEdit = payment.type === 'waiver' ? payment.waiverReason : 
                                                            payment.type === 'payment' ? payment.paymentNote || payment.description : 
                                                            payment.description;
                                          setEditNoteText(textToEdit || '');
                                          setEditNoteTournamentId(payment.tournamentId || '');
                                          setShowEditNoteModal(true);
                                        }}
                                        className="ml-2 text-gray-300 hover:text-blue-600 transition-colors opacity-0 group-hover:opacity-100"
                                        title="Edit Note"
                                      >
                                        <Edit size={12} />
                                      </button>
                                      
                                      <button 
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (window.confirm(`Are you sure you want to delete this $${payment.amount} payment record? This action cannot be undone.`)) {
                                            if (onDeleteWalletTransaction) {
                                              onDeleteWalletTransaction(payment.id, payment.userId, payment.amount);
                                            } else {
                                              alert('Delete function not provided to component.');
                                            }
                                          }
                                        }}
                                        className="ml-2 text-gray-300 hover:text-red-600 transition-colors opacity-0 group-hover:opacity-100"
                                        title="Delete Payment Record"
                                      >
                                        <Trash2 size={12} />
                                      </button>
                                    </>
                                  )}
                                </div>
                                {payment.type === 'waiver' ? (
                                  <span className="text-purple-600">🎁 Waived</span>
                                ) : payment.type === 'reimbursement_paid' ? (
                                  <span className="text-blue-600">💸 Club Settled</span>
                                ) : (
                                  <span className="text-green-600">✓ Paid</span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
        }
      </div>

      {/* Edit Payment Modal */}
      {showEditNoteModal && editNoteTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 animate-in zoom-in-95 duration-200">
            <h3 className="text-xl font-bold mb-4">
              {editNoteTarget.type === 'payment' ? 'Edit Payment' : 'Edit Note'}
            </h3>
            
            <div className={`p-4 rounded-lg mb-4 ${editNoteTarget.type === 'waiver' ? 'bg-purple-50 text-purple-900' : 'bg-green-50 text-green-900'}`}>
              <p className="font-semibold">{editNoteTarget.type === 'waiver' ? 'Waived Amount' : 'Payment Amount'}: {formatCurrency(Math.abs(editNoteTarget.amount))}</p>
              {editNoteTarget.playerName && <p className="text-sm mt-0.5 opacity-80">{editNoteTarget.playerName}</p>}
            </div>

            <div className="space-y-4 mb-6">
              {/* Tournament dropdown — only for payment transactions */}
              {editNoteTarget.type === 'payment' && tournaments && tournaments.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tournament
                  </label>
                  <select
                    value={editNoteTournamentId}
                    onChange={(e) => setEditNoteTournamentId(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-white"
                  >
                    <option value="">Not tournament-specific (e.g. Annual Membership)</option>
                    {tournaments.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Assigning a tournament lets this payment appear in the Tournament Players tab.
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {editNoteTarget.type === 'waiver' ? 'Waiver Reason' : 'Transaction Note'}
                </label>
                <textarea
                  value={editNoteText}
                  onChange={(e) => setEditNoteText(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none min-h-[100px] resize-none"
                  placeholder="Enter the updated note..."
                  autoFocus
                />
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setShowEditNoteModal(false); setEditNoteTournamentId(''); }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg font-medium transition"
              >
                Cancel
              </button>
              <button
                onClick={submitEditNote}
                disabled={!editNoteText.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50 transition"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Waiver Modal */}
      {showWaiverModal && waiverTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">Waive Fee</h3>

            <div className="bg-purple-50 p-4 rounded-lg mb-4">
              <p className="font-semibold text-purple-900">{waiverTarget.player.name}</p>
              <p className="text-sm text-purple-800">{getRegistrationFeeLabel({ name: clubName })}: {formatCurrency(waiverTarget.fee.amount)}</p>
            </div>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Waiver Type</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      checked={waiverType === 'full'}
                      onChange={() => setWaiverType('full')}
                      className="text-purple-600 focus:ring-purple-500"
                    />
                    <span>Full Amount ({formatCurrency(waiverTarget.fee.amount)})</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      checked={waiverType === 'partial'}
                      onChange={() => setWaiverType('partial')}
                      className="text-purple-600 focus:ring-purple-500"
                    />
                    <span>Partial Amount</span>
                  </label>
                </div>
              </div>

              {waiverType === 'partial' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount to Waive</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                    <input
                      type="number"
                      step="0.01"
                      value={waiverAmount}
                      onChange={(e) => setWaiverAmount(e.target.value)}
                      className="w-full pl-8 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                      placeholder="0.00"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason (Required)</label>
                <textarea
                  value={waiverReason}
                  onChange={(e) => setWaiverReason(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 outline-none h-24 resize-none"
                  placeholder="e.g. Scholarship, Board Member, Special Circumstance..."
                />
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowWaiverModal(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg font-medium"
              >
                Cancel
              </button>
              <button
                onClick={submitWaiver}
                disabled={!waiverReason.trim() || (waiverType === 'partial' && !waiverAmount)}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Confirm Waiver
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && paymentTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Record Payment</h3>
              <button
                onClick={() => setShowPaymentModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="bg-green-50 p-4 rounded-lg mb-6">
              <p className="font-semibold text-green-900">{paymentTarget.name}</p>
              <p className="text-sm text-green-800">
                Balance Due: {formatCurrency(Math.max(0, calculatePlayerOwed(paymentTarget.id).balance))}
              </p>
            </div>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Amount</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    step="0.01"
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    className="w-full pl-8 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                    placeholder="0.00"
                    autoFocus
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                >
                  <option value="Zelle">Zelle</option>
                  <option value="Venmo">Venmo</option>
                  <option value="PayPal">PayPal</option>
                  <option value="Benevity">Benevity</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Note (Required)</label>
                <textarea
                  value={paymentNote}
                  onChange={(e) => setPaymentNote(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 outline-none h-24 resize-none"
                  placeholder="e.g. Cash payment, check #123..."
                />
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowPaymentModal(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg font-medium"
              >
                Cancel
              </button>
              <button
                onClick={submitPayment}
                disabled={!paymentAmount || parseFloat(paymentAmount) <= 0 || !paymentNote.trim()}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Record Payment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reimbursement Settlement Modal */}
      {showReimbursementModal && reimbursementTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Settle Reimbursement</h3>
              <button
                onClick={() => setShowReimbursementModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg mb-6">
              <p className="font-semibold text-blue-900">{reimbursementTarget.player.name}</p>
              <p className="text-sm text-blue-800">
                Club Owes: {formatCurrency(reimbursementTarget.defaultAmount)}
              </p>
            </div>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Settlement Amount</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    step="0.01"
                    value={reimbursementAmount}
                    onChange={(e) => setReimbursementAmount(e.target.value)}
                    className="w-full pl-8 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    placeholder="0.00"
                    autoFocus
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">You can settle part or all of the owed amount.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Note (Required)</label>
                <textarea
                  value={reimbursementNote}
                  onChange={(e) => setReimbursementNote(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none h-24 resize-none"
                  placeholder="e.g. Zelle sent on Jan 1st..."
                />
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowReimbursementModal(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg font-medium"
              >
                Cancel
              </button>
              <button
                onClick={submitReimbursement}
                disabled={!reimbursementAmount || parseFloat(reimbursementAmount) <= 0 || !reimbursementNote.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Record Settlement
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Update Fee Modal */}
      {showUpdateFeeModal && updateFeeTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">Update {getRegistrationFeeLabel({ name: clubName })}</h3>

            <div className="bg-orange-50 p-4 rounded-lg mb-4">
              <p className="font-semibold text-orange-900">{updateFeeTarget.player.name}</p>
              <div className="mt-2 text-sm text-orange-800">
                <p className="font-bold">Current Total Fee: {formatCurrency(updateFeeTarget.currentTotal)}</p>
                <div className="mt-1 pl-2 border-l-2 border-orange-200">
                  {updateFeeTarget.relevantExpenses?.map(e => (
                    <div key={e.id} className="flex justify-between text-xs">
                      <span>{e.description || e.type || (e.isInitialDonation ? getRegistrationFeeLabel({ name: clubName }) : 'Registration')}</span>
                      <span>{formatCurrency(e.amountPerPlayer !== undefined ? parseFloat(e.amountPerPlayer) : parseFloat(e.amount))}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New {getRegistrationFeeLabel({ name: clubName })}</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    step="0.01"
                    value={updateFeeAmount}
                    onChange={(e) => setUpdateFeeAmount(e.target.value)}
                    className="w-full pl-8 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 outline-none"
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason for Update (Required)</label>
                <textarea
                  value={updateFeeReason}
                  onChange={(e) => setUpdateFeeReason(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 outline-none h-24 resize-none"
                  placeholder="e.g. Correcting amount, Agreed discount..."
                />
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowUpdateFeeModal(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg font-medium"
              >
                Cancel
              </button>
              <button
                onClick={submitUpdateFee}
                disabled={!updateFeeReason.trim() || !updateFeeAmount || parseFloat(updateFeeAmount) < 0}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Update Fee
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---------------------------------------------------- */}
      {/* APPROVE PAYMENT MODAL (New) */}
      {/* ---------------------------------------------------- */}
      {showApproveModal && approveTarget && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="bg-green-600 p-6 text-white text-center">
              <CheckCircle size={48} className="mx-auto mb-2 opacity-90" />
              <h3 className="text-xl font-bold">Approve Payment</h3>
              <p className="opacity-90 mt-1">
                Verify receipt of <span className="font-bold text-white">{formatCurrency(approveTarget.amount)}</span> from {approveTarget.playerName}
              </p>
            </div>

            <div className="p-6 space-y-4">
              {/* Payment Date (Read Only) */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                  Submitted On
                </label>
                <div className="p-3 bg-gray-50 rounded-lg text-gray-700 font-medium">
                  {formatDate(approveTarget.submittedAt || approveTarget.createdAt)}
                </div>
              </div>

              {/* Received Date Input */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                  Funds Received Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={approveDate}
                  onChange={(e) => setApproveDate(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all font-medium text-gray-900"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  For Zelle/Venmo, this is usually today. For Benevity, select when funds hit the bank.
                </p>
              </div>

              {/* Note Input */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                  Treasurer Note (Optional)
                </label>
                <textarea
                  value={approveNote}
                  onChange={(e) => setApproveNote(e.target.value)}
                  placeholder="e.g. Verified via Wells Fargo"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 min-h-[80px] text-sm"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowApproveModal(false)}
                  className="flex-1 py-3 text-gray-600 font-bold hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (!approveDate) {
                      alert('Please select a Received Date');
                      return;
                    }
                    await onApprovePayment(approveTarget, approveNote, approveDate);

                    // Post-approval cleanup logic to close review modal if empty
                    const remaining = paymentSubmissions.filter(s =>
                      s.status === 'pending' &&
                      s.id !== approveTarget.id &&
                      (s.playerId === reviewTarget.player.id || (reviewTarget.player.userId && s.playerId === reviewTarget.player.userId))
                    );
                    setShowApproveModal(false);
                    if (remaining.length === 0) setShowReviewModal(false);
                  }}
                  className="flex-1 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-bold shadow-md transition-all active:scale-95"
                >
                  Confirm Approval
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Settle Debt Modal */}
      {showSettleModal && settleTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full shadow-2xl overflow-hidden">
            <div className="bg-blue-600 px-6 py-4 flex justify-between items-center text-white relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full -mr-10 -mt-10 pointer-events-none"></div>
              <div>
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <CheckCircle size={20} />
                  Settle Club Debt
                </h2>
                <p className="text-blue-100 text-sm">Record payment made to player</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowSettleModal(false);
                  setSettleTarget(null);
                }}
                className="text-blue-200 hover:text-white transition-colors"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmitSettle} className="p-6">
              <div className="mb-6 pb-6 border-b border-gray-100">
                <div className="flex items-center gap-4 mb-4">
                  {settleTarget.photoURL ? (
                    <img
                      src={settleTarget.photoURL}
                      alt={settleTarget.firstName}
                      className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-sm"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xl font-bold border-2 border-white shadow-sm">
                      {(settleTarget.firstName || '').charAt(0)}{(settleTarget.lastName || '').charAt(0)}
                    </div>
                  )}
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 border-b border-gray-100 pb-1 w-full text-left">
                      {settleTarget.firstName} {settleTarget.lastName}
                    </h3>
                  </div>
                </div>

                <div className="bg-blue-50 p-4 rounded-lg flex justify-between items-center">
                  <div className="flex items-center gap-2 text-blue-700">
                    <User size={18} />
                    <span className="font-medium">Club owes</span>
                  </div>
                  <span className="text-xl font-bold text-blue-700">{formatCurrency(parseFloat(settleAmount))}</span>
                </div>
              </div>

              {error && (
                <div className="mb-6 p-4 bg-red-50 text-red-700 text-sm rounded-lg flex items-start gap-3">
                  <span className="mt-0.5 font-bold">!</span>
                  <span>{error}</span>
                </div>
              )}

              <div className="space-y-4 mb-8">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Settlement Amount *
                  </label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-500 font-medium select-none">
                      $
                    </div>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max={settleAmount}
                      value={settleAmount}
                      onChange={(e) => setSettleAmount(e.target.value)}
                      className="w-full pl-8 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all text-gray-900 font-medium"
                      required
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-2 ml-1">
                    Enter the amount you paid to {settleTarget.firstName}. Max: {formatCurrency(parseFloat(settleAmount))}.
                  </p>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowSettleModal(false)}
                  className="px-5 py-2.5 text-gray-600 hover:bg-gray-100 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isProcessing || !settleAmount || parseFloat(settleAmount) <= 0}
                  className="px-5 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2 shadow-sm shadow-blue-200"
                >
                  {isProcessing ? 'Processing' : 'Confirm Settlement'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Review Payment Modal */}
      {showReviewModal && reviewTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full p-6 shadow-xl">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="text-xl font-bold">Review Payments</h3>
                <p className="text-gray-500 text-sm mt-1">
                  Pending submissions for <span className="font-semibold text-gray-900">{reviewTarget.player.name}</span>
                </p>
              </div>
              <button
                onClick={() => setShowReviewModal(false)}
                className="text-gray-400 hover:text-gray-600 p-1 hover:bg-gray-100 rounded-full transition-colors"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
              {paymentSubmissions
                .filter(s =>
                  s.status === 'pending' &&
                  (s.playerId === reviewTarget.player.id || (reviewTarget.player.userId && s.playerId === reviewTarget.player.userId))
                )
                .map(submission => (
                  <div key={submission.id} className="relative bg-white border border-gray-200 rounded-xl p-5 shadow-sm flex flex-col md:flex-row gap-4 overflow-hidden">
                    {/* Blue Left Accent */}
                    <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-blue-500"></div>

                    <div className="flex-1 pl-2">
                      {/* Header: Name + Badge */}
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-bold text-gray-900 text-lg">
                          {submission.playerName || reviewTarget.player.name}
                        </h3>
                        <span className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded font-semibold">
                          Pending Review
                        </span>
                      </div>

                      {/* Amount */}
                      <div className="text-3xl font-extrabold text-gray-900 mb-4">
                        {formatCurrency(submission.amount)}
                      </div>

                      {/* Details Grid */}
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center">
                          <span className="w-32 text-gray-500 font-medium">Method:</span>
                          <span className="font-medium text-gray-900 uppercase">
                            {submission.paymentMethod || 'Manual'}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <span className="w-32 text-gray-500 font-medium">Payment Date:</span>
                          <span className="text-gray-900">
                            {formatDate(submission.paymentDate)}
                          </span>
                        </div>
                        <div className="flex items-start">
                          <span className="w-32 text-gray-500 font-medium pt-0.5">Notes:</span>
                          <span className="text-gray-900 italic">
                            {submission.note || submission.notes || 'No notes provided'}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <span className="w-32 text-gray-500 font-medium">Submitted:</span>
                          <span className="text-gray-500 text-xs">
                            {new Date(submission.submittedAt || submission.createdAt).toLocaleString()}
                          </span>
                        </div>
                        {submission.proofURL && (
                          <div className="flex items-center pt-1">
                            <span className="w-32 text-gray-500 font-medium">Proof:</span>
                            <a
                              href={submission.proofURL}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 font-medium underline flex items-center gap-1"
                            >
                              View Proof ↗
                            </a>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Actions - Vertical Stack */}
                    <div className="flex flex-col gap-3 justify-center min-w-[140px] pt-4 md:pt-0 border-t md:border-t-0 md:border-l border-gray-100 md:pl-4">
                      <button
                        onClick={() => {
                          setApproveTarget(submission);
                          setApproveDate(new Date().toISOString().split('T')[0]); // Default to today
                          setApproveNote('');
                          setShowApproveModal(true);
                        }}
                        className="w-full py-2.5 bg-green-500 hover:bg-green-600 text-white rounded-lg font-bold flex items-center justify-center gap-2 shadow-sm transition-colors"
                      >
                        <CheckCircle size={18} /> Approve
                      </button>

                      <button
                        onClick={async () => {
                          await onDenyPayment(submission);
                          const remaining = paymentSubmissions.filter(s =>
                            s.status === 'pending' &&
                            s.id !== submission.id &&
                            (s.playerId === reviewTarget.player.id || (reviewTarget.player.userId && s.playerId === reviewTarget.player.userId))
                          );
                          if (remaining.length === 0) setShowReviewModal(false);
                        }}
                        className="w-full py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg font-bold flex items-center justify-center gap-2 shadow-sm transition-colors"
                      >
                        <AlertCircle size={18} /> Deny
                      </button>
                    </div>
                  </div>
                ))}
            </div>

            <div className="mt-6 pt-4 border-t flex justify-end">
              <button
                onClick={() => setShowReviewModal(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentManagement;